import { useEffect, useMemo, useRef } from 'react';

import { AUDIENCE_ATTRIBUTE_FAMILIES } from '../utils/audienceRequest';

import type { Company } from '@/types/directory/Company';
import type { OptionProps } from '@amelio/ui-library';

import { AudienceFilterList } from '@/components/AudienceFilterList/AudienceFilterList';
import {
    useCompanyAttributesFilters,
    type SelectedFilters,
} from '@/hooks/filters/useCompanyAttributesFilters';
import { getCompanyAttributeList } from '@/utils/company';
import { familiesSeededBy } from '@/utils/companyAttributeFilters';

interface AudienceCriteriaPickerProps {
    company: Company;
    /** Selection the cards open with. Read once, on mount: give the picker a new `key` to seed it
     * again. Never reported back through `onFiltersChange`, which only hears the reader's changes. */
    defaultFilters: SelectedFilters | undefined;
    /** Locks every card: a closed envelope, or a reader who may not change its settings, still reads
     * its audience the way it was composed. */
    isReadOnly: boolean;
    onFiltersChange: (filters: SelectedFilters) => void;
}

/**
 * The envelope's audience composed as the evaluation cycle composes it: one card per criterion,
 * joined by "ET". An unfinished criterion stays visible instead of silently widening or emptying
 * the audience.
 */
export const AudienceCriteriaPicker = ({
    company,
    defaultFilters,
    isReadOnly,
    onFiltersChange,
}: AudienceCriteriaPickerProps) => {
    const attributes = useMemo(
        () =>
            Object.fromEntries(
                Object.entries(getCompanyAttributeList(company)).filter(([family]) =>
                    AUDIENCE_ATTRIBUTE_FAMILIES.includes(family),
                ),
            ),
        [company],
    );

    // The cards a saved audience opens on: the families its selection actually fills.
    const defaultSelectedAttributes = useMemo(
        () => (defaultFilters ? familiesSeededBy(attributes, defaultFilters) : undefined),
        [attributes, defaultFilters],
    );

    const {
        filters,
        selectedCompanyAttributes,
        onSelectCompanyAttribute,
        onSelectCompanyAttributeOption,
    } = useCompanyAttributesFilters({ company, defaultFilters, defaultSelectedAttributes });

    // The selection the cards open with is the saved one, so reporting it would turn merely opening
    // an envelope into an unsaved change. Compared by identity: the hook keeps that very object until
    // the reader changes something, including across a development double-render.
    const openingFiltersRef = useRef(filters);

    // The callback is read through a ref rather than depended on: a caller that writes it inline
    // hands a new function at every render, and re-announcing the unchanged selection to it undoes
    // whatever that caller derives from a real change — here it would redeclare the envelope's
    // audience, and with it the unsaved change, at every render of the page.
    const onFiltersChangeRef = useRef(onFiltersChange);
    onFiltersChangeRef.current = onFiltersChange;

    useEffect(() => {
        if (filters === openingFiltersRef.current) {
            return;
        }

        onFiltersChangeRef.current(filters);
    }, [filters]);

    const attributeOptions = useMemo<OptionProps[]>(
        () =>
            // Label and value are the raw name, as on the cycle screen: the list compares a card's
            // attribute to the option labels, and each row decodes the name for display.
            Object.values(attributes).map((attribute) => ({
                label: attribute.name,
                value: attribute.name,
            })),
        [attributes],
    );

    return (
        <AudienceFilterList
            companyAttributes={attributeOptions}
            selectedCompanyAttributes={selectedCompanyAttributes}
            filters={filters}
            accent={'violet'}
            isReadOnly={isReadOnly}
            onSelectCompanyAttribute={onSelectCompanyAttribute}
            onSelectCompanyAttributeOption={onSelectCompanyAttributeOption}
        />
    );
};
